import type { ReactNode } from "react";
import { Button } from "react-aria-components";

import bannerFoldBack from "@/assets/figma/merchant-home/banner-fold-back.svg";
import bannerFoldFront from "@/assets/figma/merchant-home/banner-fold-front.svg";
import foldBack from "@/assets/figma/merchant-home/fold-back.svg";
import foldFront from "@/assets/figma/merchant-home/fold-front.svg";
import arrow from "@/assets/figma/chat-add-menu/arrow-right.svg";
import { cn } from "@/shared/lib/cn";
import type { MerchantHomeMetric } from "@/features/merchant-home/ui/merchantHomeModels";

export function HomeIcon({ src, className }: { src: string; className?: string }) {
  return <img alt="" aria-hidden="true" draggable="false" className={cn("merchant-home__icon", className)} src={src} />;
}

export function HomeGlass({ children, className, ...props }: { children: ReactNode; className?: string; "data-testid"?: string }) {
  return <div {...props} className={cn("merchant-home__glass", className)}><span aria-hidden="true" className="merchant-home__glass-surface" /><div className="merchant-home__glass-content">{children}</div><span aria-hidden="true" className="merchant-home__glass-stroke" /></div>;
}

export function HomeMetric({ metric }: { metric: MerchantHomeMetric }) {
  return <div className="merchant-home__metric"><p className="merchant-home__eyebrow">{metric.label}</p><p className="merchant-home__metric-value"><strong>{metric.value}</strong>{metric.unit && <span>{metric.unit}</span>}</p><div className="merchant-home__metric-comparison">{metric.change && <span className="merchant-home__change" data-tone={metric.tone ?? "neutral"}>{metric.direction === "down" ? "↓ " : metric.direction === "up" ? "↑ " : ""}{metric.change}</span>}{metric.comparison && <span>{metric.comparison}</span>}</div></div>;
}

export function HomeCarouselControls({ count, current, onSelect, label, showArrows = true }: { count: number; current: number; onSelect: (index: number) => void; label: string; showArrows?: boolean }) {
  if (count < 2) return null;
  return <div className="merchant-home__carousel" aria-label={`${label}切换`} onKeyDown={event => {
    if (!(event.target instanceof HTMLElement)) return;
    const focused = event.target.closest<HTMLButtonElement>(".merchant-home__carousel-indicator");
    if (!focused) return;
    const buttons = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>(".merchant-home__carousel-indicator"));
    const index = buttons.indexOf(focused);
    if (index < 0) return;
    const next = event.key === "ArrowRight" ? (index + 1) % count
      : event.key === "ArrowLeft" ? (index - 1 + count) % count
        : event.key === "Home" ? 0 : event.key === "End" ? count - 1 : null;
    if (next === null) return;
    event.preventDefault(); event.stopPropagation();
    onSelect(next);
    buttons[next]?.focus();
  }}>
    {showArrows && <Button className="merchant-home__carousel-arrow" aria-label={`上一项${label}`} onPress={() => onSelect((current - 1 + count) % count)}><HomeIcon src={arrow} className="merchant-home__arrow-back" /></Button>}
    <div className="merchant-home__carousel-indicators">{Array.from({ length: count }, (_, index) => <Button className="merchant-home__carousel-indicator" key={index} aria-label={`第 ${index + 1} 项${label}`} aria-current={current === index ? "true" : undefined} onPress={() => onSelect(index)}><span /></Button>)}</div>
    {showArrows && <Button className="merchant-home__carousel-arrow" aria-label={`下一项${label}`} onPress={() => onSelect((current + 1) % count)}><HomeIcon src={arrow} /></Button>}
  </div>;
}

export function HomeFold({ variant = "default" }: { variant?: "default" | "banner" }) {
  return <div aria-hidden="true" className={cn("merchant-home__fold", variant === "banner" && "merchant-home__fold--banner")}><img src={variant === "banner" ? bannerFoldBack : foldBack} alt="" className="merchant-home__fold-back" /><img src={variant === "banner" ? bannerFoldFront : foldFront} alt="" className="merchant-home__fold-front" /></div>;
}
